import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { DataGrid, type Column } from '../components/ui/DataGrid';
import { useToast } from '../components/ui/Toast';
import { PageHeader } from '../components/ui/PageHeader';

interface Alert {
  id: string;
  product_id: string;
  status: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  current_stock: number;
  threshold: number;
  created_at: string;
}

/** Screens 27-28 — Stock alerts (priority-sorted). PUR + OWN. */
export default function StockAlerts() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [rows, setRows] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try {
      const { data } = await apiClient.get<Alert[]>('/stock-alerts?status=ACTIVE');
      setRows(data);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const ack = async (id: string) => {
    try {
      await apiClient.post(
        `/stock-alerts/${id}/acknowledge`,
        {},
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({ tone: 'success', message: t('warehouse.alerts.acknowledged') });
      await load();
    } catch (err) {
      showApiError(err);
    }
  };

  const columns: Column<Alert>[] = [
    {
      key: 'priority',
      header: t('warehouse.alerts.priority'),
      render: (r) => (
        <span
          className={`text-xs px-1.5 py-0.5 rounded ${
            {
              CRITICAL: 'bg-destructive/20 text-destructive',
              HIGH: 'bg-destructive/10 text-destructive',
              MEDIUM: 'bg-warning/20 text-warning',
              LOW: 'bg-muted text-foreground',
            }[r.priority]
          }`}
        >
          {t(`warehouse.alerts.levels.${r.priority}`, { defaultValue: r.priority })}
        </span>
      ),
    },
    {
      key: 'product_id',
      header: t('common.product'),
      render: (r) => <span className="font-mono text-xs">{r.product_id}</span>,
    },
    { key: 'current_stock', header: t('warehouse.alerts.current') },
    { key: 'threshold', header: t('warehouse.alerts.threshold') },
    { key: 'created_at', header: t('warehouse.alerts.since') },
    {
      key: 'id',
      header: '',
      render: (r) => (
        <button onClick={() => ack(r.id)} className="text-xs border rounded px-2 py-0.5">
          {t('warehouse.alerts.ack')}
        </button>
      ),
      sortable: false,
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('warehouse.alerts.title')}
        description={t('warehouse.alerts.description')}
      />
      <DataGrid columns={columns} rows={rows} loading={loading} rowKey={(r) => r.id} />
    </div>
  );
}
